import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Bot, Cpu, CircuitBoard, Code2, Zap, BrainCircuit, ArrowRight, Settings } from 'lucide-react';

const RoboticsEngineering: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Apex Robotics Lab";
    
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-600 selection:text-white overflow-hidden">
            {/* Navbar */}
            <nav className="fixed w-full z-50 px-8 py-6 bg-white/80 backdrop-blur-md border-b border-slate-200">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center text-white shadow-sm">
                            <Bot size={24} />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-slate-900">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex gap-10 font-mono text-xs font-bold text-slate-500 uppercase tracking-widest">
                        <a href="#" className="hover:text-blue-600 transition-colors">Research</a>
                        <a href="#" className="hover:text-blue-600 transition-colors">Dynamics</a>
                        <a href="#" className="hover:text-blue-600 transition-colors">Perception</a>
                        <a href="#" className="hover:text-blue-600 transition-colors">Control</a>
                    </div>
                    
                    <Button variant="outline" className="font-mono text-xs border-slate-300 hover:border-blue-600 hover:text-blue-600 uppercase tracking-widest h-10 px-6 rounded-sm">
                        Lab Access
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative pt-32 pb-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-[10px] font-mono font-bold uppercase tracking-widest mb-8 border border-blue-100">
                            <Zap size={12} /> Systems Live • v4.2.0
                        </div>
                        
                        <h1 className="text-6xl md:text-8xl font-bold tracking-tighter text-slate-900 mb-8 leading-[0.9]">
                            Building <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Autonomy.</span>
                        </h1>
                        
                        <p className="text-lg text-slate-500 font-normal leading-relaxed max-w-md mb-12 border-l-2 border-slate-200 pl-6">
                            Pushing the boundaries of bipedal locomotion, computer vision, and human-robot interaction.
                        </p>
                        
                        <div className="flex gap-4">
                            <Button className="bg-slate-900 text-white hover:bg-blue-600 rounded-sm h-14 px-8 font-mono text-xs font-bold uppercase tracking-widest transition-all">
                                View Projects
                            </Button>
                            <Button variant="ghost" className="text-slate-600 hover:bg-slate-100 rounded-sm h-14 px-8 font-mono text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                Read Papers <ArrowRight size={14} />
                            </Button>
                        </div>
                    </div>

                    {/* Technical Graphic */}
                    <div className="relative h-[600px] bg-white border border-slate-200 rounded-sm p-4 shadow-xl">
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                        
                        {/* Simulation Viewport */}
                        <div className="relative h-full w-full bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center">
                            <div className="absolute top-4 left-4 font-mono text-[10px] text-slate-400">
                                CAM_01: RES 4K <br/>
                                FPS: 120 <br/>
                                LATENCY: 2ms
                            </div>
                            
                            {/* Abstract Robot Arm SVG/Div Construction */}
                            <div className="relative w-64 h-64">
                                <div className="absolute top-1/2 left-1/2 w-4 h-32 bg-slate-300 -translate-x-1/2 -translate-y-full origin-bottom animate-[spin_5s_linear_infinite]">
                                     <div className="absolute -top-4 -left-2 w-8 h-8 rounded-full border-2 border-blue-500"></div>
                                </div>
                                <div className="absolute top-1/2 left-1/2 w-32 h-4 bg-slate-300 -translate-x-full -translate-y-1/2 origin-right animate-[spin_7s_linear_infinite_reverse]">
                                     <div className="absolute -left-4 -top-2 w-8 h-8 rounded-full border-2 border-indigo-500"></div>
                                </div>
                                <div className="absolute top-1/2 left-1/2 w-8 h-8 bg-slate-800 rounded-full -translate-x-1/2 -translate-y-1/2 border-4 border-white z-10 shadow-lg"></div>
                            </div>

                            {/* Code Overlay */}
                            <div className="absolute bottom-0 left-0 w-full p-4 bg-slate-900/90 backdrop-blur text-green-400 font-mono text-[10px] leading-tight">
                                <span className="text-blue-400">def</span> <span className="text-yellow-400">initialize_kinematics</span>(self):<br/>
                                &nbsp;&nbsp;self.joints = []<br/>
                                &nbsp;&nbsp;<span className="text-purple-400">return</span> True
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Research Areas */}
            <section className="py-24 px-8 border-t border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                         <div className="md:col-span-1">
                             <h2 className="text-3xl font-bold mb-4 text-slate-900">Lab Focus</h2>
                             <p className="text-slate-500 text-sm leading-relaxed mb-6">
                                 Our interdisciplinary approach combines mechanical engineering, electrical engineering, and computer science.
                             </p>
                             <div className="w-12 h-1 bg-blue-600"></div>
                         </div>
                         <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { icon: BrainCircuit, title: "Machine Learning", desc: "Reinforcement learning for complex motor control tasks." },
                                { icon: CircuitBoard, title: "Embedded Systems", desc: "Real-time processing on custom FPGA architectures." },
                                { icon: Settings, title: "Mechatronics", desc: "Actuator design and soft robotics materials." }
                            ].map((item, i) => (
                                <div key={i} className="group cursor-pointer">
                                    <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 mb-6 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all rounded-sm">
                                        <item.icon size={24} />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                         </div>
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-slate-900 py-20 px-8 text-slate-400 border-t border-slate-800">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
                     <div>
                         <div className="flex items-center gap-2 mb-6">
                             <Cpu size={24} className="text-blue-500" />
                             <span className="font-bold text-white text-lg tracking-tight">{schoolName}</span>
                         </div>
                         <div className="font-mono text-xs">
                             <p>Room 404, Engineering Block B</p>
                             <p>Institute of Technology</p>
                         </div>
                     </div>
                     
                     <div className="grid grid-cols-2 gap-12 font-mono text-xs uppercase tracking-widest">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white">GitHub</a></li>
                             <li><a href="#" className="hover:text-white">Publications</a></li>
                             <li><a href="#" className="hover:text-white">Wiki</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white">Faculty</a></li>
                             <li><a href="#" className="hover:text-white">Sponsors</a></li>
                             <li><a href="#" className="hover:text-white">Contact</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-slate-800 font-mono text-[10px] text-slate-600">
                     SYSTEM.READY | © 2026 {schoolName}
                 </div>
            </footer>
        </div>
    );
};

export default RoboticsEngineering;
